import { useMemo } from 'react'

import { type CrossClientQueryResult } from '../../../models/CrossClient'
import { getIntegrationDisplayName } from '../../../common/integrations'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useGetMeQuery } from '../../../store/user/user'

import { type CrmAction, type CrmActionsMap } from './CrossClientActionsDropdown'
import { CRM_ACTION_REGISTRY, type CrmActionKind } from './crmActionRegistry'
import { useCrmActionHost } from './useCrmActionHost'
import { useActionTracking, type CrossClientActionScope } from './useActionTracking'

export const useCrmActions = (results: CrossClientQueryResult[], scope: CrossClientActionScope = 'single'): CrmActionsMap => {
  const accountId = useAppSelector(selectAccountId)
  const { data: currentUser } = useGetMeQuery({ accountId })
  const { open } = useCrmActionHost()
  const { trackActionClicked } = useActionTracking(scope)

  return useMemo<CrmActionsMap>(() => {
    const connectedServiceIds = new Set((currentUser?.integrations ?? []).map((integration) => integration.serviceId))

    const map: CrmActionsMap = {}
    for (const [kind, descriptor] of Object.entries(CRM_ACTION_REGISTRY) as [CrmActionKind, (typeof CRM_ACTION_REGISTRY)[CrmActionKind]][]) {
      if (!connectedServiceIds.has(descriptor.serviceId)) continue
      const action: CrmAction = {
        label: descriptor.label,
        icon: descriptor.icon,
        onClick: () => {
          trackActionClicked(getIntegrationDisplayName(descriptor.serviceId), descriptor.label)
          open(kind, { resolveResults: () => Promise.resolve(results), scope })
        },
      }
      const existing = map[descriptor.serviceId] ?? []
      map[descriptor.serviceId] = [...existing, action]
    }
    return map
  }, [results, scope, currentUser?.integrations, open, trackActionClicked])
}
